import { StateMachine } from "@/core/arch/state_machine";
import type { MoneyRepository } from "./money_repository";
import type { Transaction, Receipt, Invoice } from "./types";

// ── State ─────────────────────────────────────────────────────────────────────

export interface TransactionDetailState {
  status: "loading" | "loaded" | "error" | "not-found";
  transaction: Transaction | null;
  receipts: Receipt[];
  invoice: Invoice | null;
  error?: string;
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

export class TransactionDetailBloc extends StateMachine<TransactionDetailState> {
  constructor(private repo: MoneyRepository) {
    super({
      status: "loading",
      transaction: null,
      receipts: [],
      invoice: null,
    });
  }

  /**
   * Fans out three parallel fetches on mount:
   * 1. getTransaction — primary, blocking. null → not-found. error → full-page error.
   * 2. listReceiptsForTransaction — non-blocking, silent degradation.
   * 3. getInvoice — fires only if tx.invoiceId is set; non-blocking, silent degradation.
   *
   * Main page renders as soon as Transaction resolves; secondary cards fill in separately.
   */
  async load(txId: string): Promise<void> {
    this.setState({
      ...this.state,
      status: "loading",
      transaction: null,
      receipts: [],
      invoice: null,
      error: undefined,
    });

    // Fire primary
    let transaction: Transaction | null = null;
    try {
      transaction = await this.repo.getTransaction(txId);
    } catch (err) {
      this.setState({
        ...this.state,
        status: "error",
        error:
          err instanceof Error ? err.message : "Failed to load transaction",
      });
      return;
    }

    if (!transaction) {
      this.setState({ ...this.state, status: "not-found" });
      return;
    }

    // Primary resolved — render immediately while secondaries are in flight
    this.setState({
      ...this.state,
      status: "loaded",
      transaction,
      receipts: [],
      invoice: null,
    });

    // Fire secondaries in parallel — both silently degrade on failure
    const [receiptsResult, invoiceResult] = await Promise.allSettled([
      this.repo.listReceiptsForTransaction(txId),
      transaction.invoiceId
        ? this.repo.getInvoice(transaction.invoiceId)
        : Promise.resolve(null),
    ]);

    const receipts =
      receiptsResult.status === "fulfilled" ? receiptsResult.value : [];
    const invoice =
      invoiceResult.status === "fulfilled" ? invoiceResult.value : null;

    this.setState({
      ...this.state,
      receipts,
      invoice,
    });
  }

  async retry(txId: string): Promise<void> {
    await this.load(txId);
  }
}
